import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createLeaveRequest, getLeaveBalances, getLeaveTypes } from '../api/leave';
import { useAuth } from '../store/auth';
import PageHeader from '../components/ui/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

type LeaveType = {
  id: string;
  displayName: string;
  type: string;
};

type BalanceMap = Record<string, number>; // leaveTypeId → remaining days

export default function LeaveNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<BalanceMap>({});
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [halfDay, setHalfDay] = useState<'FULL' | 'FIRST_HALF' | 'SECOND_HALF'>('FULL');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submittingRef = useRef(false);

  useEffect(() => {
    setError('');
    getLeaveTypes()
      .then((res) => {
        if (res.error) { setError(res.error); return; }
        const data = res.data;
        if (Array.isArray(data)) {
          setTypes(data);
          if (data[0]) setLeaveTypeId(data[0].id);
        }
      })
      .catch(() => setError('Failed to load leave types'));

    if (user?.employeeId) {
      getLeaveBalances(user.employeeId, new Date().getFullYear())
        .then((res) => {
          if (!res.data) return;
          const items = Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
          const map: BalanceMap = {};
          for (const b of items) {
            const remaining = (b.remainingDays ?? b.totalAvailable ?? 0);
            if (b.leaveType?.id) map[b.leaveType.id] = remaining;
          }
          setBalances(map);
        })
        .catch(() => {/* balance is optional — ignore errors */});
    }
  }, [user?.employeeId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    submittingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const res = await createLeaveRequest({
        leaveTypeId,
        startDate,
        endDate,
        halfDay,
        reason: reason || undefined,
      });

      if (res.data?.leaveRequest || res.data?.message) {
        navigate('/leave', { replace: true });
        return;
      }

      if (res.error) {
        const overlap =
          res.error.toLowerCase().includes('overlapping') ||
          res.error.toLowerCase().includes('overlap');
        if (overlap) {
          navigate('/leave', { replace: true });
          return;
        }
        setError(res.error);
        return;
      }

      setError('Unexpected response from server. Check My leave — your request may still have been saved.');
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const fieldClass =
    'flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-lg space-y-6">
      <PageHeader title="Apply for leave" subtitle="Submit a new leave request" />
      <Card>
        <CardContent className="space-y-4 p-4">
        {error && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <label className="block">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm font-medium">Type of leave *</span>
            {leaveTypeId && balances[leaveTypeId] !== undefined && (
              <span className={`text-xs font-medium ${balances[leaveTypeId] <= 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {balances[leaveTypeId] <= 0
                  ? 'No balance remaining'
                  : `${balances[leaveTypeId]} day${balances[leaveTypeId] !== 1 ? 's' : ''} remaining`}
              </span>
            )}
          </div>
          <select
            value={leaveTypeId}
            onChange={(e) => setLeaveTypeId(e.target.value)}
            className={fieldClass}
            required
          >
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">Start date</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={fieldClass}
            required
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">End date</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            className={fieldClass}
            required
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">Duration</span>
          <select
            value={halfDay}
            onChange={(e) => setHalfDay(e.target.value as 'FULL' | 'FIRST_HALF' | 'SECOND_HALF')}
            className={fieldClass}
          >
            <option value="FULL">Full day</option>
            <option value="FIRST_HALF">Half day — first half (AM)</option>
            <option value="SECOND_HALF">Half day — second half (PM)</option>
          </select>
          {halfDay !== 'FULL' && (
            <p className="mt-1 text-xs text-muted-foreground">
              {halfDay === 'FIRST_HALF' ? '08:30 – 12:30' : '12:30 – 16:30'} · deducts 0.5 day
            </p>
          )}
        </label>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">Reason (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className={fieldClass}
          />
        </label>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={() => navigate('/leave')} className="flex-1">
          Cancel
        </Button>
        <Button type="submit" disabled={loading || !leaveTypeId} className="flex-1">
          {loading ? 'Submitting…' : 'Submit request'}
        </Button>
      </div>
    </form>
  );
}
